<template>
  <v-container fluid>
    <v-row v-if="isAdminUser">
      <v-col cols="12" md="4">
        <BaseSelect
          v-model="selectedArea"
          :items="areaOptions"
          label="Filtrar por área"
          clearable
          @change="filterByArea"
        />
      </v-col>
      <v-col cols="12" md="4">
        <v-row class="mx-0" justify="end">
          <v-col>
            <BaseInput v-model="searchQuery" label="Buscar notificaciones" clearable @input="filterNotifications" />
          </v-col>
          <v-col cols="auto" class="pl-2">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <span v-bind="attrs" v-on="on">
                  <IconButton variant="secondary" icon="mdi-download" @click="downloadNotificationsCsv" />
                </span>
              </template>
              <span>Descargar CSV</span>
            </v-tooltip>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
    <v-row v-if="!isAdminUser">
      <v-col cols="12" md="4">
        <v-select
          v-model="selectedEstatus"
          :items="estatusOptions"
          background-color="primary"
          color="secondary"
          rounded
          filled
          dense
          item-color="accent"
          item-text="text"
          item-value="value"
          label="Filtrar por Estatus de lectura"
          @change="filterByEstatus"
        />
      </v-col>
      <v-col cols="12" md="4">
        <v-row align="center" class="mx-0" justify="end">
          <v-col>
            <BaseInput v-model="searchQuery" label="Buscar notificaciones" clearable @input="filterNotifications" />
          </v-col>
          <v-col cols="auto" class="pl-2">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <span v-bind="attrs" v-on="on">
                  <IconButton variant="secondary" icon="mdi-download" @click="downloadNotificationsCsv" />
                </span>
              </template>
              <span>Descargar CSV</span>
            </v-tooltip>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
    <v-row v-if="loading" justify="center" class="my-5">
      <v-col cols="12" class="text-center">
        <v-progress-circular indeterminate color="primary" size="64" />
        <p class="mt-3">Cargando notificaciones...</p>
      </v-col>
    </v-row>
    <v-row v-else>
      <v-col cols="12">
        <NotificationsTable :notifications="filteredNotifications" />
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import NotificationsTable from '@src/components/notifications/NotificationsTable.vue'
import BaseSelect from '@src/components/base/BaseSelect.vue'
import BaseInput from '@src/components/base/BaseInput.vue'
import IconButton from '@src/components/base/IconButton.vue'
import downloadCsv from '@/utils/downloadCsv'
import AREAS from '@src/modules/notificaciones/domain/areas'
import { mapActions, mapGetters } from 'vuex'
import { PERFILES } from '@src/perfiles'
import AREAS_PERFILES from '@/modules/notificaciones/domain/AreasPerfiles.js'

const AREA_NOTIFICACION_KEYS = {
  [AREAS.RECURSOS_HUMANOS]: 'recursos_humanos',
  [AREAS.ARMERIA]: 'armeria',
  [AREAS.EQUIPAMIENTO]: 'equipamiento',
  [AREAS.IFCSP]: 'ifcsp',
  [AREAS.DESARROLLO_POLICIAL]: 'desarrollo_policial',
  [AREAS.ENLACE_C3]: 'enlace_c3',
  [AREAS.REGISTRO_PERSONAL]: 'registro_personal',
  [AREAS.ENLACE_COMISARIAS]: 'enlace_comisarias',
  [AREAS.EJECUTIVO]: 'ejecutivo',
}

export default {
  components: { NotificationsTable, BaseSelect, BaseInput, IconButton },

  data() {
    return {
      selectedArea: null,
      selectedEstatus: null,
      searchQuery: '',
      filteredNotifications: [],
      PERFILES,
      estatusOptions: [
        { text: 'Leídas', value: 'read' },
        { text: 'No leídas', value: 'unread' },
      ],
    }
  },

  created() {
    this.fetchAll()
  },

  computed: {
    ...mapGetters('notificaciones', { notifications: 'all', loading: 'loading' }),

    areaOptions() {
      return Object.values(AREAS)
    },

    areaKey() {
      const perfil = this.$store.getters['getPerfil']
      for (const [areaKey, perfiles] of Object.entries(AREAS_PERFILES)) {
        if (Array.isArray(perfiles) && perfiles.includes(perfil)) {
          return areaKey
        }
      }
      return null
    },
  },

  watch: {
    notifications: {
      immediate: true,
      handler(newNotifications) {
        this.filteredNotifications = newNotifications
      },
    },
  },

  methods: {
    ...mapActions('notificaciones', ['fetchAll']),
    downloadNotificationsCsv() {
      const rows = (this.filteredNotifications || []).map((n) => ({
        Fecha: n.fecha || n.fechaHora || n.createdAt || n.created_at || '',
        Título: n.titulo || '',
        Mensaje: n.mensaje || '',
        Prioridad: n.prioridad?.title || n.prioridad || '',
        Categoría: n.categoria?.title || n.categoria || '',
      }))

      downloadCsv(rows, { filename: 'notificaciones' })
    },

    applyFilters() {
      let notifications = [...this.notifications]

      if (this.selectedArea) {
        notifications = notifications.filter((notification) => notification.titulo.includes(this.selectedArea))
      }

      if (this.selectedEstatus) {
        const keyProp = this.areaKey ? AREA_NOTIFICACION_KEYS[this.areaKey] : null
        if (keyProp) {
          notifications = notifications.filter((notification) => {
            if (this.selectedEstatus === 'read') {
              return notification[keyProp] === 2
            } else if (this.selectedEstatus === 'unread') {
              return notification[keyProp] !== 2
            }
            return true
          })
        }
      }

      if (this.searchQuery && this.searchQuery.trim() !== '') {
        const query = this.searchQuery.toLowerCase().trim()
        notifications = notifications.filter((notification) => {
          const titulo = notification.titulo?.toLowerCase() || ''
          const descripcion = notification.descripcion?.toLowerCase() || ''
          const mensaje = notification.mensaje?.toLowerCase() || ''

          return titulo.includes(query) || descripcion.includes(query) || mensaje.includes(query)
        })
      }

      this.filteredNotifications = notifications
    },

    filterByArea() {
      this.applyFilters()
    },

    filterByEstatus() {
      this.applyFilters()
    },

    filterNotifications() {
      this.applyFilters()
    },
  },
}
</script>
